<template>
    <!--
      This section displays each time restriction rule for the selected device.
      Conditions:
      - Only shown if a device (configuration) is selected (`currentConfiguration`).
      - Only shown when not editing a restriction (`!editingRestrictionId`).
      - Each rule displays:
          - Rule name and type icon (global usage or quota).
          - Time settings (start/end, personal control, max per day/week).
          - Associated apps in a compact, draggable view.
          - Edit and delete controls.
      - Global Usage/Night Break rules are sorted to appear first.
    -->
    <v-row
        :class="{ 'global-usage-bar': isGlobalUsageRule(restriction) }"
        class="pl-3"
    >
        <!-- Left: Rule Info and Time Settings -->
        <v-col class="mb-0 pb-0" cols="7">
            <!-- Title based on restriction type -->
            <div class="text-h6 font-weight-regular text-left mt-4 mb-2 d-flex">
                <v-icon class="mr-2">
                    {{ restriction.type === 'exclusive' ? icons.mdiClockOutline : icons.mdiChartPie }}
                </v-icon>
                <div class="d-flex align-center">
                    {{ getSystemOrNormalRuleName(restriction) }}
                </div>
            </div>

            <v-card :class="{ 'global-usage-bar': isGlobalUsageRule(restriction) }"
                    class="mb-0 usage-rule-card" flat>
                <v-card-text class="py-0">
                    <v-row>
                        <v-col class="mb-0 pb-0" cols="12">
                            <div class="d-flex align-center">
                <span class="body-1">
                  <template>
                    <span v-if="restriction.type === 'quota' && restriction.weekDays"
                          class="weekdays-preview ml-4">
                      <span
                          v-for="(day, index) in weekDaysArray"
                          :key="index"
                          :class="{ 'grey--text': !isDaySelected(index, restriction.weekDays) }"
                      >{{ day }}</span>
                    </span>
                    <span>
                      {{ restriction.startTime + ' - ' + restriction.endTime }}
                    </span>
                    <v-icon v-if="restriction.allowedTime" class="ml-2 mr-1" small
                            v-text="icons.mdiClockOutline"/>
                    <span v-if="restriction.allowedTime">
                      {{ secondsToHhmm(restriction.allowedTime) + ' ' + $gettext('allowed') }}
                    </span>
                    <template v-if="restriction.maxByDay && restriction.type === 'quota'">
                      ({{ $gettext('Max per day:') + ' ' + restriction.maxByDay }})
                    </template>
                    <template v-if="restriction.maxByWeek && restriction.type === 'quota'">
                      ({{ $gettext('Max per week:') + ' ' + restriction.maxByWeek }})
                    </template>
                  </template>
                </span>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Edit form for time restriction settings -->
                    <v-row v-if="isEditingTimeRestrictions && restriction.id === editingRestrictionId">
                        <v-col cols="12">
                            <div>
                                <div v-if="restriction.type === 'quota'" class="mb-4">
                                    <div class="subtitle-1 mb-2 text-left">
                                        {{ $gettext('Please pick the day(s)') }}
                                    </div>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-checkbox
                                                :input-value="restriction.weekDays && restriction.weekDays.length === 7"
                                                :label="$gettext('All Days')" class="mb-2" dense
                                                hide-details @change="() => toggleAllDays(restriction)"/>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-checkbox v-model="restriction.weekDays"
                                                        :label="$gettext('Monday')" :value="1" class="mb-2" dense
                                                        hide-details></v-checkbox>
                                            <v-checkbox v-model="restriction.weekDays"
                                                        :label="$gettext('Tuesday')" :value="2" class="mb-2" dense
                                                        hide-details></v-checkbox>
                                            <v-checkbox v-model="restriction.weekDays"
                                                        :label="$gettext('Wednesday')" :value="3" class="mb-2" dense
                                                        hide-details></v-checkbox>
                                            <v-checkbox v-model="restriction.weekDays"
                                                        :label="$gettext('Thursday')" :value="4" class="mb-2" dense
                                                        hide-details></v-checkbox>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-checkbox v-model="restriction.weekDays"
                                                        :label="$gettext('Friday')" :value="5" class="mb-2" dense
                                                        hide-details></v-checkbox>
                                            <v-checkbox v-model="restriction.weekDays"
                                                        :label="$gettext('Saturday')" :value="6" class="mb-2" dense
                                                        hide-details></v-checkbox>
                                            <v-checkbox v-model="restriction.weekDays"
                                                        :label="$gettext('Sunday')" :value="7" class="mb-2" dense
                                                        hide-details></v-checkbox>
                                        </v-col>
                                    </v-row>
                                </div>
                                <div>
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <v-text-field :label="$gettext('Overall Time (HH:MM)')"
                                                          :value="secondsToHhmm(restriction.allowedTime)" dense
                                                          outlined placeholder="HH:MM"
                                                          @input="val => updateAllowedTimeFromHhmm(val, restriction)"></v-text-field>
                                        </v-col>
                                        <v-col v-if="restriction.type === 'quota'" cols="12" sm="4">
                                            <v-text-field v-model.number="restriction.maxByDay"
                                                          :label="$gettext('Max/Day')" dense min="1" outlined
                                                          type="number"
                                                          @change="val => sanitizeTimeValue(val, 'maxByDay', restriction)"
                                                          @input="val => sanitizeTimeValue(val, 'maxByDay', restriction)"></v-text-field>
                                        </v-col>
                                        <v-col v-if="restriction.type === 'quota'" cols="12" sm="4">
                                            <v-text-field v-model.number="restriction.maxByWeek"
                                                          :label="$gettext('Max/Week')" dense min="1" outlined
                                                          type="number"
                                                          @change="val => sanitizeTimeValue(val, 'maxByWeek', restriction)"
                                                          @input="val => sanitizeTimeValue(val, 'maxByWeek', restriction)"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-col>

        <!-- Right: Apps, Edit, Delete -->
        <v-col class="mb-0 pb-0" cols="5" style="max-height: 96px; overflow: clip;">
            <div class="d-flex flex-wrap mb-0 pb-0">
                <v-row class="text-left">
                    <v-col :class="{ 'droppable-highlight': isDragging }" cols="9">
                        <div class="d-flex flex-wrap draggable-container"
                             @end="$emit('drag-ended')" @start="$emit('drag-started')">
                            <div v-for="app in restriction.apps" :key="app.packageId"
                                 class="text-center mr-0 pr-0 app-icon-container">
                                <app-icon-for-usage-time
                                    :app-name="app.appName"
                                    :device-name="currentSelectedConfiguration"
                                    :folder-name="getPhoneTypeDirForDevice(chosenModel)"
                                    :hide-app-name="true" :package-name="app.packageId"
                                    :show-menu="showMenu" :x="x" :y="y" class="mx-auto"
                                    @changeShowMenu="updateShowMenu"
                                    @removeAppFromRestriction="() => removeAppFromRestriction(app.packageId, restriction)"
                                />
                            </div>
                        </div>
                    </v-col>
                    <v-col class="text-center px-0" cols="3">
                        <v-icon class="text-right primary--text mr-2"
                                @click="$emit('edit', restriction.id)"
                                v-text="icons.mdiPencilCircleOutline"></v-icon>
                        <v-icon v-if="restriction.type !== 'exclusive'" class="ml-auto primary--text"
                                @click="$emit('delete', restriction)"
                                v-text="icons.mdiDeleteCircleOutline"></v-icon>
                    </v-col>
                </v-row>
            </div>
        </v-col>
    </v-row>
</template>

<script>

import AppIconForUsageTime from "../../devices/AppIconForUsageTime.vue";

export default {
    name: "RestrictionConciseView",
    components: {AppIconForUsageTime},
    props: {
        restriction: Object,
        icons: Object,
        weekDaysArray: Array,
        isDaySelected: Function,
        secondsToHhmm: Function,
        $gettext: Function,
        isEditingTimeRestrictions: Boolean,
        editingRestrictionId: [Number, String],
        showMenu: Object,
        x: Number,
        y: Number,
        isDragging: Boolean,
        currentSelectedConfiguration: [String, Number],
        chosenModel: [String, Number, Object],
        getPhoneTypeDirForDevice: Function,
        updateShowMenu: Function,
        removeAppFromRestriction: Function,
        getSystemOrNormalRuleName: Function,
        isGlobalUsageRule: Function,
        toggleAllDays: Function,
        updateAllowedTimeFromHhmm: Function,
        sanitizeTimeValue: Function,
    }
};
</script>

<style scoped>
.app-icon-container {
    width: 72px;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.global-usage-bar {
    background: #e3f2fd;
}

.weekdays-preview {
    font-family: monospace;
    letter-spacing: 1px;
    span {
        font-size: 14px;
        font-weight: 500;
    }
}

.draggable-container {
    transition: all 0.2s ease !important;
    min-height: 100px !important;
    width: 100% !important;
    border: 2px dashed transparent !important;
    border-radius: 4px !important;
    padding: 8px !important;
}

.droppable-highlight {
    border: 2px dashed #1976d2;
    background-color: rgba(25, 118, 210, 0.1);
}


.app-icon-container {
    width: 72px;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;

    ::v-deep .v-avatar {
        width: 48px !important;
        height: 48px !important;
        min-width: 48px !important;
        min-height: 48px !important;
    }
}

.app-icon-container-concise {
    width: 54px;
}
</style>
